/**
 * Desktop widget faces (plan 12 §8): a glanceable face per frame type in
 * three fixed sizes, drawn from `previewSource(frame)` (and the task store
 * for the board); a face that edits in place (Todo) reads the content it
 * saves. The widget shell owns the material and sets the ink
 * (`--widget-ink`, `--widget-muted`, `--widget-line`, `--widget-fill`); a
 * face fills its box. Types without a face render the live frame instead.
 */
import type { ComponentType } from "react";
import type { FrameType } from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";
import { AgendaWidgetFace } from "../agenda-frame/agenda-widget-face";
import { FlightWidgetFace } from "../flight-frame/flight-widget-face";
import { TaskboardWidgetFace } from "../taskboard-frame/taskboard-widget-face";
import { TodoWidgetFace } from "../todo-frame/todo-widget-face";
import { WeatherWidgetFace } from "../weather-frame/weather-widget-face";
import { GithubWidgetFace } from "../github-frame/github-widget-face";

export type WidgetSize = "small" | "medium" | "large";

export interface WidgetFaceProps {
  frame: FrameData;
  size: WidgetSize;
}

/**
 * Each size in logical points; a pinned widget is one of them unless its
 * type is sized freely (`widget-sizing.ts`).
 * Apple's macOS desktop widgets, measured (plan 12 §8 Sizes): a small card
 * is 164 pt square inside its 180 pt window; medium and large span two grid
 * cells across (and down) with Apple's 16 pt gap between cards.
 */
export const WIDGET_SIZES: Readonly<
  Record<WidgetSize, { readonly width: number; readonly height: number }>
> = {
  small: { width: 164, height: 164 },
  medium: { width: 344, height: 164 },
  large: { width: 344, height: 344 },
};

/** The widget sizes, smallest first. */
export const WIDGET_SIZE_ORDER: readonly WidgetSize[] = [
  "small",
  "medium",
  "large",
];

/** How a frame type shows on the desktop. */
export interface WidgetFace {
  /** Its glance face; null: the live frame, sized to the widget. */
  Face: ComponentType<WidgetFaceProps> | null;
  /**
   * It has parts of its own to act on (ticks, controls, editable text, a
   * graph, scrolling content): a click acts there and never opens the frame,
   * which stays in the ⋯ menu. Without any, a click opens it in Focus.
   */
  interactive: boolean;
}

/** Every type not listed: the live frame, interactive (Doc, Note, Code, …). */
const LIVE_FRAME: WidgetFace = { Face: null, interactive: true };

const WIDGET_FACES: Partial<Record<FrameType, WidgetFace>> = {
  agenda: { Face: AgendaWidgetFace, interactive: false },
  weather: { Face: WeatherWidgetFace, interactive: false },
  todo: { Face: TodoWidgetFace, interactive: true },
  taskboard: { Face: TaskboardWidgetFace, interactive: false },
  flight: { Face: FlightWidgetFace, interactive: false },
  // Its rows open their pull request, run or commit.
  github: { Face: GithubWidgetFace, interactive: true },
  // A live frame with nothing to act on: a glance, opened by a click.
  event: { Face: null, interactive: false },
};

/** How a frame of `frameType` shows on the desktop. */
export function widgetFaceFor(frameType: FrameType): WidgetFace {
  return WIDGET_FACES[frameType] ?? LIVE_FRAME;
}
